
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea, ReferenceLine, Area, AreaChart, Bar, BarChart } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { TrendingUp, TrendingDown, Target, ZoomIn, ZoomOut, RotateCcw, Activity, Zap, BarChart3 } from 'lucide-react';

interface GrowthPoint {
  tradeNumber: number;
  growth: number;
  prediction: 'WIN' | 'LOSS';
  actual: number;
  change: number;
  isCorrect: boolean;
  cumulative: number;
  originalPosition: number;
}

interface GrowthChartProps {
  positionsData: Array<{
    position: number;
    prediction: 'WIN' | 'LOSS' | 'SKIP';
    actualResult: number;
    isCorrect?: boolean;
  }>;
  tpRatio?: number;
  slRatio?: number;
}

export const GrowthChart: React.FC<GrowthChartProps> = ({ positionsData, tpRatio = 2, slRatio = 1 }) => {
  const [zoomDomain, setZoomDomain] = useState<{left?: number; right?: number; top?: number; bottom?: number} | null>(null);
  const [refAreaLeft, setRefAreaLeft] = useState<number | null>(null);
  const [refAreaRight, setRefAreaRight] = useState<number | null>(null);
  const [chartMode, setChartMode] = useState<'line' | 'area' | 'volume'>('area');
  const [animationProgress, setAnimationProgress] = useState(0);

  // Animation effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationProgress(100);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const { growthData, stats } = useMemo(() => {
    // Filter out SKIP predictions first
    const activePositions = positionsData.filter(position => position.prediction !== 'SKIP');
    
    let cumulativeGrowth = 0;
    let maxGrowth = 0;
    let minGrowth = 0;
    let totalPredictions = 0;
    let wins = 0;
    let losses = 0;

    const data: GrowthPoint[] = activePositions.map((position, index) => {
      let change = 0;
      
      totalPredictions++;
      
      // Apply TP/SL logic based on prediction vs actual result
      if (position.prediction === 'LOSS' && position.actualResult === 0) {
        // Prediction Loss → Actual Loss (correct) = +SL
        change = slRatio;
        wins++;
      } else if (position.prediction === 'LOSS' && position.actualResult === 1) {
        // Prediction Loss → Actual Win (wrong, but reverse) = -TP
        change = -tpRatio;
        losses++;
      } else if (position.prediction === 'WIN' && position.actualResult === 1) {
        // Prediction Win → Actual Win (correct) = +TP
        change = tpRatio;
        wins++;
      } else if (position.prediction === 'WIN' && position.actualResult === 0) {
        // Prediction Win → Actual Loss (wrong) = -SL
        change = -slRatio;
        losses++;
      }
      
      cumulativeGrowth += change;
      
      maxGrowth = Math.max(maxGrowth, cumulativeGrowth);
      minGrowth = Math.min(minGrowth, cumulativeGrowth);

      return {
        tradeNumber: index + 1, // Re-indexed from 1
        growth: cumulativeGrowth,
        prediction: position.prediction as 'WIN' | 'LOSS',
        actual: position.actualResult,
        change,
        isCorrect: position.isCorrect ?? false,
        cumulative: cumulativeGrowth,
        originalPosition: position.position
      };
    });

    const finalGrowth = cumulativeGrowth;
    const totalRange = maxGrowth - minGrowth;
    const maxDrawdown = maxGrowth - minGrowth;

    return {
      growthData: data,
      stats: {
        finalGrowth,
        maxGrowth,
        minGrowth,
        totalRange,
        maxDrawdown,
        totalPredictions,
        wins,
        losses,
        winRate: totalPredictions > 0 ? (wins / totalPredictions) * 100 : 0
      }
    };
  }, [positionsData]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload as GrowthPoint;
    
    return (
      <div className="bg-background border rounded-lg shadow-lg p-3">
        <p className="font-medium mb-2">Trade #{label}</p>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between items-center">
            <span>Growth:</span>
            <Badge variant={data.growth >= 0 ? 'default' : 'destructive'}>
              {data.growth > 0 ? '+' : ''}{data.growth}
            </Badge>
          </div>
          <div className="flex justify-between items-center">
            <span>Change:</span>
            <span className={data.change > 0 ? 'text-green-600' : data.change < 0 ? 'text-red-600' : 'text-muted-foreground'}>
              {data.change > 0 ? '+' : ''}{data.change.toFixed(1)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span>Prediction:</span>
            <Badge variant={data.prediction === 'WIN' ? 'default' : 'destructive'}>
              {data.prediction}
            </Badge>
          </div>
          <div className="flex justify-between items-center">
            <span>Actual:</span>
            <Badge variant={data.actual === 1 ? 'default' : 'destructive'}>
              {data.actual === 1 ? 'WIN' : 'LOSS'}
            </Badge>
          </div>
          <div className="flex justify-between items-center">
            <span>Result:</span>
            <Badge variant={data.isCorrect ? 'default' : 'destructive'}>
              {data.isCorrect ? '✓ Correct' : '✗ Incorrect'}
            </Badge>
          </div>
          <div className="flex justify-between items-center text-xs text-muted-foreground">
            <span>Original Position:</span>
            <span>#{data.originalPosition}</span>
          </div>
        </div>
      </div>
    );
  };

  // Zoom handlers
  const handleMouseDown = useCallback((e: any) => {
    if (e && e.activeLabel) {
      setRefAreaLeft(e.activeLabel);
    }
  }, []);

  const handleMouseMove = useCallback((e: any) => {
    if (refAreaLeft && e && e.activeLabel) {
      setRefAreaRight(e.activeLabel);
    }
  }, [refAreaLeft]);

  const handleMouseUp = useCallback(() => {
    if (refAreaLeft && refAreaRight) {
      const left = Math.min(refAreaLeft, refAreaRight);
      const right = Math.max(refAreaLeft, refAreaRight);
      
      if (Math.abs(right - left) > 1) {
        setZoomDomain({ left, right });
      }
    }
    setRefAreaLeft(null);
    setRefAreaRight(null);
  }, [refAreaLeft, refAreaRight]);

  const handleZoomOut = useCallback(() => {
    setZoomDomain(null);
  }, []);

  const handleReset = useCallback(() => {
    setZoomDomain(null);
    setRefAreaLeft(null);
    setRefAreaRight(null);
  }, []);

  return (
    <Card className="relative overflow-hidden border-0 bg-gradient-to-br from-background via-background to-muted/20">
      {/* Animated background particles */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-10 left-10 w-2 h-2 bg-primary/20 rounded-full animate-pulse" style={{ animationDelay: '0s' }} />
        <div className="absolute top-20 right-20 w-1 h-1 bg-primary/30 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-20 left-20 w-1.5 h-1.5 bg-primary/25 rounded-full animate-pulse" style={{ animationDelay: '2s' }} />
        <div className="absolute bottom-10 right-10 w-1 h-1 bg-primary/20 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
        <div className="absolute top-1/2 left-1/3 w-1 h-1 bg-primary/15 rounded-full animate-pulse" style={{ animationDelay: '1.5s' }} />
        
        {/* Geometric patterns */}
        <div className="absolute top-5 right-5 w-8 h-8 border border-primary/10 rotate-45" />
        <div className="absolute bottom-5 left-5 w-6 h-6 border border-primary/5 rotate-12" />
      </div>
      
      {/* Glass morphism effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-background/5 to-transparent" />
      
      <CardHeader className="relative z-10">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              Growth Performance Analytics
              <Badge variant="secondary" className="ml-2">
                <Zap className="h-3 w-3 mr-1" />
                Live
              </Badge>
            </CardTitle>
            <CardDescription className="mt-1">
              Real-time cumulative growth tracking with TP:{tpRatio} SL:{slRatio} ratio
            </CardDescription>
          </div>
          
          {/* Chart mode switcher */}
          <div className="flex gap-1 bg-muted/50 rounded-lg p-1">
            <Button
              variant={chartMode === 'area' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setChartMode('area')}
              className="h-8 px-2"
            >
              Area
            </Button>
            <Button
              variant={chartMode === 'line' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setChartMode('line')}
              className="h-8 px-2"
            >
              Line
            </Button>
            <Button
              variant={chartMode === 'volume' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setChartMode('volume')}
              className="h-8 px-2"
            >
              <BarChart3 className="h-3 w-3" />
            </Button>
          </div>
        </div>
        
        {/* Progress indicator */}
        <div className="mt-4">
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>Analytics Progress</span>
            <span>{animationProgress}%</span>
          </div>
          <Progress value={animationProgress} className="h-1" />
        </div>
      </CardHeader>
      <CardContent>
        {/* Enhanced Statistics Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="group p-4 bg-gradient-to-br from-background to-muted/20 rounded-xl border border-primary/10 hover:border-primary/20 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5">
            <div className="flex items-center justify-between mb-2">
              <Target className="h-5 w-5 text-primary group-hover:scale-110 transition-transform" />
              <div className="flex gap-1">
                <div className="w-1 h-6 bg-primary/20 rounded" />
                <div className="w-1 h-4 bg-primary/30 rounded" />
                <div className="w-1 h-5 bg-primary/40 rounded" />
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Final Growth</p>
              <p className={`text-2xl font-bold transition-colors ${stats.finalGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {stats.finalGrowth > 0 ? '+' : ''}{stats.finalGrowth}
              </p>
              <div className="mt-1">
                <Progress 
                  value={Math.abs(stats.finalGrowth) / Math.max(Math.abs(stats.maxGrowth), Math.abs(stats.minGrowth)) * 100} 
                  className="h-1" 
                />
              </div>
            </div>
          </div>

          <div className="group p-4 bg-gradient-to-br from-green-50/50 to-green-100/20 dark:from-green-950/20 dark:to-green-900/10 rounded-xl border border-green-200/50 hover:border-green-300/50 transition-all duration-300 hover:shadow-lg hover:shadow-green-500/5">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="h-5 w-5 text-green-600 group-hover:scale-110 transition-transform" />
              <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300">
                ATH
              </Badge>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Peak Growth</p>
              <p className="text-2xl font-bold text-green-600">
                +{stats.maxGrowth}
              </p>
              <p className="text-xs text-green-600/70 mt-1">
                All-time high
              </p>
            </div>
          </div>

          <div className="group p-4 bg-gradient-to-br from-red-50/50 to-red-100/20 dark:from-red-950/20 dark:to-red-900/10 rounded-xl border border-red-200/50 hover:border-red-300/50 transition-all duration-300 hover:shadow-lg hover:shadow-red-500/5">
            <div className="flex items-center justify-between mb-2">
              <TrendingDown className="h-5 w-5 text-red-600 group-hover:scale-110 transition-transform" />
              <Badge variant="secondary" className="text-xs bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300">
                ATL
              </Badge>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Lowest Point</p>
              <p className="text-2xl font-bold text-red-600">
                {stats.minGrowth}
              </p>
              <p className="text-xs text-red-600/70 mt-1">
                Max drawdown
              </p>
            </div>
          </div>

          <div className="group p-4 bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl border border-primary/20 hover:border-primary/30 transition-all duration-300 hover:shadow-lg hover:shadow-primary/10">
            <div className="flex items-center justify-between mb-2">
              <Activity className="h-5 w-5 text-primary group-hover:scale-110 transition-transform" />
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                <span className="text-xs text-primary">Live</span>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Win Rate</p>
              <p className="text-2xl font-bold text-primary">
                {stats.winRate.toFixed(1)}%
              </p>
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-muted-foreground">
                  {stats.wins}W / {stats.losses}L
                </p>
                <Progress value={stats.winRate} className="h-1 w-16" />
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Controls Section */}
        <div className="flex items-center justify-between mb-6 p-3 bg-muted/20 rounded-xl border border-muted-foreground/10">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleZoomOut}
              disabled={!zoomDomain}
              className="hover:bg-primary/10 hover:border-primary/20"
            >
              <ZoomOut className="h-4 w-4 mr-1" />
              Zoom Out
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              className="hover:bg-primary/10 hover:border-primary/20"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Reset View
            </Button>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-gradient-to-r from-green-400 to-green-600 rounded-full" />
                <span className="text-muted-foreground">Profit Zone</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-gradient-to-r from-red-400 to-red-600 rounded-full" />
                <span className="text-muted-foreground">Loss Zone</span>
              </div>
            </div>
            
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <ZoomIn className="h-4 w-4" />
              {zoomDomain ? (
                <Badge variant="secondary">Zoomed View</Badge>
              ) : (
                'Drag to zoom in'
              )}
            </div>
          </div>
        </div>

        {/* Enhanced Growth Chart */}
        {growthData.length > 0 ? (
          <div className="h-96 relative">
            {/* Chart background with gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-muted/5 via-transparent to-transparent rounded-lg" />
            
            <ResponsiveContainer width="100%" height="100%">
              {chartMode === 'area' ? (
                <AreaChart 
                  data={growthData} 
                  margin={{ top: 20, right: 40, left: 20, bottom: 40 }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                >
                  <defs>
                    <linearGradient id="growthGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.8}/>
                      <stop offset="50%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgb(34, 197, 94)" stopOpacity={0.2}/>
                      <stop offset="100%" stopColor="rgb(34, 197, 94)" stopOpacity={0.05}/>
                    </linearGradient>
                    <linearGradient id="lossGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgb(239, 68, 68)" stopOpacity={0.05}/>
                      <stop offset="100%" stopColor="rgb(239, 68, 68)" stopOpacity={0.2}/>
                    </linearGradient>
                  </defs>
                  
                  <CartesianGrid 
                    strokeDasharray="1 3" 
                    opacity={0.2} 
                    stroke="hsl(var(--muted-foreground))"
                  />
                  <XAxis 
                    dataKey="tradeNumber"
                    type="number"
                    scale="linear"
                    domain={zoomDomain ? [zoomDomain.left, zoomDomain.right] : ['dataMin', 'dataMax']}
                    label={{ value: 'Trade Number', position: 'insideBottom', offset: -10, style: { textAnchor: 'middle', fontSize: '12px', fill: 'hsl(var(--muted-foreground))' } }}
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis 
                    domain={zoomDomain ? [zoomDomain.bottom, zoomDomain.top] : ['auto', 'auto']}
                    label={{ value: 'Cumulative Growth', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: '12px', fill: 'hsl(var(--muted-foreground))' } }}
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  
                  {/* Enhanced reference lines and areas */}
                  <ReferenceLine 
                    y={0} 
                    stroke="hsl(var(--border))" 
                    strokeDasharray="4 4" 
                    strokeWidth={2}
                    opacity={0.8}
                  />
                  
                  {/* Dynamic profit/loss zones */}
                  <ReferenceArea 
                    y1={0} 
                    y2={stats.maxGrowth + 1} 
                    fill="url(#profitGradient)"
                  />
                  <ReferenceArea 
                    y1={stats.minGrowth - 1} 
                    y2={0} 
                    fill="url(#lossGradient)"
                  />

                  {/* Target lines */}
                  {stats.maxGrowth > 5 && (
                    <ReferenceLine 
                      y={stats.maxGrowth * 0.8} 
                      stroke="rgb(34, 197, 94)" 
                      strokeDasharray="2 2" 
                      opacity={0.6}
                      label={{ value: "80% of Peak", position: "right", fontSize: 10 }}
                    />
                  )}

                  {/* Zoom selection */}
                  {refAreaLeft && refAreaRight && (
                    <ReferenceArea
                      x1={refAreaLeft}
                      x2={refAreaRight}
                      strokeOpacity={0.4}
                      fillOpacity={0.1}
                      fill="hsl(var(--primary))"
                      stroke="hsl(var(--primary))"
                    />
                  )}
                  
                  <Area 
                    type="monotone" 
                    dataKey="growth" 
                    stroke="hsl(var(--primary))"
                    strokeWidth={3}
                    fill="url(#growthGradient)"
                    dot={false}
                    connectNulls={false}
                    animationDuration={2000}
                    animationBegin={0}
                  />
                </AreaChart>
              ) : chartMode === 'line' ? (
                <LineChart 
                  data={growthData} 
                  margin={{ top: 20, right: 40, left: 20, bottom: 40 }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                >
                  <CartesianGrid strokeDasharray="1 3" opacity={0.2} />
                  <XAxis 
                    dataKey="tradeNumber"
                    type="number"
                    scale="linear"
                    domain={zoomDomain ? [zoomDomain.left, zoomDomain.right] : ['dataMin', 'dataMax']}
                    label={{ value: 'Trade Number', position: 'insideBottom', offset: -10 }}
                  />
                  <YAxis 
                    domain={zoomDomain ? [zoomDomain.bottom, zoomDomain.top] : ['auto', 'auto']}
                    label={{ value: 'Growth', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  
                  <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" />
                  
                  {refAreaLeft && refAreaRight && (
                    <ReferenceArea
                      x1={refAreaLeft}
                      x2={refAreaRight}
                      strokeOpacity={0.3}
                      fillOpacity={0.1}
                      fill="hsl(var(--primary))"
                    />
                  )}
                  
                  <Line 
                    type="monotone" 
                    dataKey="growth" 
                    stroke="hsl(var(--primary))"
                    strokeWidth={3}
                    dot={{ r: 2, fill: 'hsl(var(--primary))' }}
                    connectNulls={false}
                    animationDuration={2000}
                  />
                </LineChart>
              ) : (
                <BarChart 
                  data={growthData} 
                  margin={{ top: 20, right: 40, left: 20, bottom: 40 }}
                >
                  <CartesianGrid strokeDasharray="1 3" opacity={0.2} />
                  <XAxis 
                    dataKey="tradeNumber"
                    label={{ value: 'Trade Number', position: 'insideBottom', offset: -10 }}
                  />
                  <YAxis 
                    label={{ value: 'Change per Trade', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  
                  <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" />
                  
                  <Bar 
                    dataKey="change" 
                    fill="hsl(var(--primary))"
                    radius={[2, 2, 0, 0]}
                    animationDuration={1500}
                  />
                </BarChart>
              )}
            </ResponsiveContainer>
            
            {/* Performance indicators */}
            <div className="absolute top-4 right-4 flex gap-2">
              {stats.finalGrowth > stats.maxGrowth * 0.8 && (
                <Badge variant="default" className="bg-green-500/90 text-white animate-pulse">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  Strong Performance
                </Badge>
              )}
              {stats.winRate > 70 && (
                <Badge variant="secondary" className="bg-primary/90 text-white">
                  <Target className="h-3 w-3 mr-1" />
                  High Accuracy
                </Badge>
              )}
            </div>
          </div>
        ) : (
          <div className="h-96 flex items-center justify-center text-muted-foreground relative">
            {/* Animated background for empty state */}
            <div className="absolute inset-0 bg-gradient-to-br from-muted/10 via-transparent to-muted/5 rounded-lg" />
            <div className="text-center relative z-10">
              <div className="relative">
                <TrendingUp className="h-16 w-16 mx-auto mb-6 opacity-30 animate-pulse" />
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary/20 rounded-full animate-ping" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No Growth Data Available</h3>
              <p className="text-sm mb-4">Waiting for prediction data to generate growth analytics</p>
              <div className="flex items-center justify-center gap-2 text-xs">
                <div className="w-2 h-2 bg-primary/30 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-primary/30 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-primary/30 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        
        {/* Additional insights section */}
        {growthData.length > 0 && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-muted/10 rounded-lg border border-muted-foreground/10">
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                <Activity className="h-4 w-4 text-primary" />
                Volatility Analysis
              </h4>
              <p className="text-xs text-muted-foreground mb-2">
                Growth range: {stats.totalRange} points
              </p>
              <Progress value={(stats.totalRange / (stats.totalPredictions || 1)) * 20} className="h-1" />
            </div>
            
            <div className="p-4 bg-muted/10 rounded-lg border border-muted-foreground/10">
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                <Target className="h-4 w-4 text-primary" />
                Consistency Score
              </h4>
              <p className="text-xs text-muted-foreground mb-2">
                Based on win rate stability
              </p>
              <Progress value={stats.winRate} className="h-1" />
            </div>
            
            <div className="p-4 bg-muted/10 rounded-lg border border-muted-foreground/10">
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-primary" />
                Trade Volume
              </h4>
              <p className="text-xs text-muted-foreground mb-2">
                {stats.totalPredictions} total predictions
              </p>
              <Progress value={Math.min(stats.totalPredictions / 100 * 100, 100)} className="h-1" />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
